import React from 'react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { Headline } from '../Headline';
import { AnimatedNumber, Reveal, usePrefersReducedMotion } from '../motion';

const INK = '#0e1c2b';

const SectionWrapper = styled('section')`
  background: #fff;
  position: relative;
`;

const Inner = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 88px 24px 80px;

  @media (max-width: 960px) {
    padding: 64px 24px 56px;
  }
`;

const HeaderGrid = styled('div')`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: center;
  margin-bottom: 48px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 8px;
    text-align: center;
    margin-bottom: 40px;
  }
`;

const HeaderCopy = styled('div')`
  padding-left: 48px;

  @media (max-width: 960px) {
    padding-left: 0;
  }
`;

const FoundationBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 560px;
  margin-top: 16px;

  @media (max-width: 960px) {
    margin: 16px auto 0;
  }
`;

const FoundationDescription = styled('p')`
  font-size: 17px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

const GlobeStage = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 960px) {
    margin-top: 8px;
  }
`;

const GlobeSvg = styled('svg')`
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;

  @keyframes orbitDashFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -18;
    }
  }

  .orbit {
    fill: none;
    stroke: rgba(24, 144, 255, 0.28);
    stroke-width: 1;
    stroke-dasharray: 2 7;
    animation: orbitDashFlow 2.2s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  .wire {
    fill: none;
    stroke: rgba(24, 144, 255, 0.32);
    stroke-width: 1;
  }

  @keyframes globeBreathe {
    0%,
    100% {
      opacity: 0.75;
    }
    50% {
      opacity: 1;
    }
  }

  .glow {
    animation: globeBreathe 5s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const ProductionBadge = styled('div')`
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  border: 1px solid #bae7ff;
  border-radius: 100px;
  background: #fff;
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  letter-spacing: 0.08em;
  color: ${INK};
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.12);

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #52c41a;
  }
`;

const C = 200;
const R = 108;

const orbitPath = (rx: number, ry: number) =>
  `M ${C - rx} ${C} a ${rx} ${ry} 0 1 0 ${rx * 2} 0 a ${rx} ${ry} 0 1 0 ${-rx * 2} 0`;

const ORBITS = [
  { rx: 168, ry: 58, tilt: -16, dur: '11s', begin: '0s' },
  { rx: 188, ry: 74, tilt: 14, dur: '14s', begin: '-5s' },
  { rx: 150, ry: 48, tilt: 36, dur: '9s', begin: '-2s' }
] as const;

const GlobeIllustration = () => {
  const reduced = usePrefersReducedMotion();

  return (
    <GlobeStage aria-hidden>
      <GlobeSvg viewBox="0 0 400 400">
        <defs>
          <radialGradient id="globeGlow" cx="50%" cy="42%" r="60%">
            <stop offset="0%" stopColor="rgba(24, 144, 255, 0.18)" />
            <stop offset="55%" stopColor="rgba(24, 144, 255, 0.06)" />
            <stop offset="100%" stopColor="rgba(24, 144, 255, 0)" />
          </radialGradient>
          <pattern
            id="globeDots"
            width="13"
            height="13"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="1.6" cy="1.6" r="1.6" fill="rgba(24, 144, 255, 0.22)" />
          </pattern>
          {ORBITS.map((orbit, index) => (
            <path
              key={index}
              id={`foundation-orbit-${index}`}
              d={orbitPath(orbit.rx, orbit.ry)}
            />
          ))}
        </defs>

        <circle className="glow" cx={C} cy={C} r={R + 26} fill="url(#globeGlow)" />
        <circle cx={C} cy={C} r={R} fill="url(#globeDots)" />
        <circle className="wire" cx={C} cy={C} r={R} />

        <ellipse className="wire" cx={C} cy={C} rx={R * 0.66} ry={R} />
        <ellipse className="wire" cx={C} cy={C} rx={R * 0.33} ry={R} />
        <ellipse className="wire" cx={C} cy={C} rx={R} ry={R * 0.33} />
        <ellipse className="wire" cx={C} cy={C} rx={R} ry={R * 0.66} />

        {ORBITS.map((orbit, index) => (
          <g key={index} transform={`rotate(${orbit.tilt} ${C} ${C})`}>
            <use className="orbit" href={`#foundation-orbit-${index}`} />
            {!reduced && (
              <g>
                <circle r="7" fill="rgba(24, 144, 255, 0.15)" />
                <circle r="3" fill={COLORS.PRIMARY} />
                <animateMotion
                  dur={orbit.dur}
                  begin={orbit.begin}
                  repeatCount="indefinite"
                >
                  <mpath xlinkHref={`#foundation-orbit-${index}`} />
                </animateMotion>
              </g>
            )}
          </g>
        ))}
      </GlobeSvg>
      <ProductionBadge>5+ years powering publishers</ProductionBadge>
    </GlobeStage>
  );
};

const StatsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled('div')`
  text-align: center;
`;

const StatValue = styled('div')`
  font-size: 44px;
  font-weight: 800;
  color: ${INK};
  line-height: 1.1;
  margin-bottom: 10px;

  @media (max-width: 960px) {
    font-size: 36px;
  }
`;

const StatLabel = styled('div')`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.PRIMARY};
  margin-bottom: 6px;
`;

const StatCaption = styled('div')`
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  max-width: 220px;
  margin: 0 auto;
`;

export const DataFoundationSection = () => (
  <SectionWrapper id="data-foundation">
    <Inner>
      <Reveal distance={24}>
        <HeaderGrid>
          <HeaderCopy>
            <Headline component="h2" variant="h3">
              Network reports alone won&apos;t tell you what your inventory is
              worth
            </Headline>
            <FoundationBody>
              <FoundationDescription>
                Network aggregation tells you what you earned. Affilimate
                builds a proprietary commerce dataset around your portfolio:
                what pages and links drive revenue, what products they point
                to, and the context your team already tags. Then the campaign
                layer puts it to work, helping your team find opportunities,
                package inventory, launch campaigns, and prove results.
              </FoundationDescription>
            </FoundationBody>
          </HeaderCopy>
          <GlobeIllustration />
        </HeaderGrid>
        <StatsGrid>
          <Stat>
            <StatValue>
              <AnimatedNumber value={70} format={(n) => Math.round(n) + 'M+'} />
            </StatValue>
            <StatLabel>Product catalog</StatLabel>
            <StatCaption>
              Matched to your links at portfolio scale. Keeping it current is
              the hard part.
            </StatCaption>
          </Stat>
          <Stat>
            <StatValue>
              <AnimatedNumber value={100} format={(n) => Math.round(n) + '+'} />
            </StatValue>
            <StatLabel>Network integrations</StatLabel>
            <StatCaption>
              Proven at scale with the world&apos;s biggest publishers
            </StatCaption>
          </Stat>
          <Stat>
            <StatValue>
              <AnimatedNumber value={200} format={(n) => Math.round(n) + '+'} />
            </StatValue>
            <StatLabel>Premium publishers</StatLabel>
            <StatCaption>
              Trusted by leading news and media publishers
            </StatCaption>
          </Stat>
          <Stat>
            <StatValue>
              $<AnimatedNumber value={3} format={(n) => n.toFixed(0) + 'B+'} />
            </StatValue>
            <StatLabel>GMV delivered</StatLabel>
            <StatCaption>
              Annual GMV publishers on Affilimate deliver for brands
            </StatCaption>
          </Stat>
        </StatsGrid>
      </Reveal>
    </Inner>
  </SectionWrapper>
);
